/**
 * Pre-stocked scenario quick-load buttons.
 *
 * One-click presets that load a Build A + Build B + Environment combo,
 * letting the user (or demo presenter) skip typing inputs and jump
 * straight to a compelling comparison.
 *
 * Eight starter scenarios spanning hunting + target audiences.
 * More can be added without changing this file's structure.
 */

import type { Build, Environment } from '../../lib/trajectory/types';

export interface Scenario {
  id: string;
  title: string;
  subtitle: string;
  category: 'western-elk' | 'whitetail-stand' | 'plains-game' | 'target' | 'mountain-mule';
  buildA: Build;
  buildB: Build;
  env: Environment;
  rangeYards: number;
}

const BASE_HUNTING_BOW = {
  bowIboFps: 342,
  bowDrawWeightLb: 70,
  bowDrawLengthIn: 29,
};

const SCENARIOS: ReadonlyArray<Scenario> = [
  {
    id: 'western-light-vs-heavy',
    title: 'Light-fast vs heavy-slow at 60y',
    subtitle: '12 mph crosswind — the canonical western elk question',
    category: 'western-elk',
    buildA: {
      ...BASE_HUNTING_BOW, arrowGrains: 380, arrowLengthIn: 28, spine: 340,
      fOCPercent: 12, pointGrains: 100, pointType: 'single-bevel', insertGrains: 50,
      vaneGeometry: 'DCA Sabre', vaneOffsetDeg: 2, vaneCount: 3, tuneQualityYawDeg: 2,
    },
    buildB: {
      ...BASE_HUNTING_BOW, arrowGrains: 510, arrowLengthIn: 28.5, spine: 250,
      fOCPercent: 17, pointGrains: 150, pointType: 'single-bevel', insertGrains: 75,
      vaneGeometry: 'DCA Sabre', vaneOffsetDeg: 2, vaneCount: 3, tuneQualityYawDeg: 2,
    },
    env: {
      windMph: 12, windAngleDeg: 90, altitudeFt: 9500,
      shotElevationDeg: 0, shotScenario: 'broadside-near',
    },
    rangeYards: 60,
  },
  {
    id: 'tune-clean-vs-dirty',
    title: 'Same arrow, clean tune vs 9° yaw',
    subtitle: 'The demo nobody else can run',
    category: 'whitetail-stand',
    buildA: {
      ...BASE_HUNTING_BOW, arrowGrains: 460, arrowLengthIn: 28.5, spine: 300,
      fOCPercent: 14, pointGrains: 100, pointType: 'COC fixed', insertGrains: 50,
      vaneGeometry: 'AAE Talon', vaneOffsetDeg: 2, vaneCount: 3, tuneQualityYawDeg: 2,
    },
    buildB: {
      ...BASE_HUNTING_BOW, arrowGrains: 460, arrowLengthIn: 28.5, spine: 300,
      fOCPercent: 14, pointGrains: 100, pointType: 'COC fixed', insertGrains: 50,
      vaneGeometry: 'AAE Talon', vaneOffsetDeg: 2, vaneCount: 3, tuneQualityYawDeg: 9,
    },
    env: {
      windMph: 0, windAngleDeg: 90, altitudeFt: 0,
      shotElevationDeg: 0, shotScenario: 'broadside-near',
    },
    rangeYards: 60,
  },
  {
    id: 'altitude-sea-vs-mountain',
    title: 'Sea level vs 10,000 ft, same build',
    subtitle: 'Why western tapes don\'t match home-range tapes',
    category: 'mountain-mule',
    buildA: {
      ...BASE_HUNTING_BOW, arrowGrains: 450, arrowLengthIn: 28, spine: 300,
      fOCPercent: 14, pointGrains: 100, pointType: 'COC fixed', insertGrains: 50,
      vaneGeometry: 'AAE Talon', vaneOffsetDeg: 2, vaneCount: 3, tuneQualityYawDeg: 2,
    },
    buildB: {
      ...BASE_HUNTING_BOW, arrowGrains: 450, arrowLengthIn: 28, spine: 300,
      fOCPercent: 14, pointGrains: 100, pointType: 'COC fixed', insertGrains: 50,
      vaneGeometry: 'AAE Talon', vaneOffsetDeg: 2, vaneCount: 3, tuneQualityYawDeg: 2,
    },
    env: {
      windMph: 0, windAngleDeg: 90, altitudeFt: 10000,
      shotElevationDeg: 0, shotScenario: 'broadside-near',
    },
    rangeYards: 80,
  },
  {
    id: 'plains-light-vs-heavy',
    title: 'Plains game — 500gr vs 575gr at 40y',
    subtitle: 'Where mass starts paying for itself',
    category: 'plains-game',
    buildA: {
      ...BASE_HUNTING_BOW, arrowGrains: 500, arrowLengthIn: 28, spine: 300,
      fOCPercent: 14, pointGrains: 100, pointType: 'single-bevel', insertGrains: 50,
      vaneGeometry: 'DCA Sabre', vaneOffsetDeg: 2, vaneCount: 3, tuneQualityYawDeg: 2,
    },
    buildB: {
      ...BASE_HUNTING_BOW, arrowGrains: 575, arrowLengthIn: 28.5, spine: 250,
      fOCPercent: 17, pointGrains: 150, pointType: 'single-bevel', insertGrains: 75,
      vaneGeometry: 'DCA Sabre', vaneOffsetDeg: 2, vaneCount: 3, tuneQualityYawDeg: 2,
    },
    env: {
      windMph: 8, windAngleDeg: 90, altitudeFt: 4000,
      shotElevationDeg: 0, shotScenario: 'broadside-near',
    },
    rangeYards: 40,
  },
  {
    id: 'whitetail-22y-swirl',
    title: 'Stand shot 22y, 5 mph swirl',
    subtitle: 'At stand range, does mass even matter?',
    category: 'whitetail-stand',
    buildA: {
      ...BASE_HUNTING_BOW, arrowGrains: 380, arrowLengthIn: 28, spine: 340,
      fOCPercent: 12, pointGrains: 100, pointType: 'fixed mech', insertGrains: 50,
      vaneGeometry: 'AAE Talon', vaneOffsetDeg: 2, vaneCount: 3, tuneQualityYawDeg: 2,
    },
    buildB: {
      ...BASE_HUNTING_BOW, arrowGrains: 460, arrowLengthIn: 28.5, spine: 300,
      fOCPercent: 14, pointGrains: 100, pointType: 'fixed mech', insertGrains: 50,
      vaneGeometry: 'AAE Talon', vaneOffsetDeg: 2, vaneCount: 3, tuneQualityYawDeg: 2,
    },
    env: {
      windMph: 5, windAngleDeg: 90, altitudeFt: 800,
      shotElevationDeg: -10, shotScenario: 'broadside-near',
    },
    rangeYards: 22,
  },
  {
    id: 'target-vs-hunting-50y',
    title: 'Target 350gr vs hunting 460gr at 50y',
    subtitle: '12 mph — what each rig trades off',
    category: 'target',
    buildA: {
      ...BASE_HUNTING_BOW, arrowGrains: 350, arrowLengthIn: 28, spine: 400,
      fOCPercent: 9, pointGrains: 80, pointType: 'COC fixed', insertGrains: 30,
      vaneGeometry: 'DCA Sabre', vaneOffsetDeg: 1, vaneCount: 3, tuneQualityYawDeg: 1,
      measuredVelocityFps: 320,
    },
    buildB: {
      ...BASE_HUNTING_BOW, arrowGrains: 460, arrowLengthIn: 28.5, spine: 300,
      fOCPercent: 14, pointGrains: 100, pointType: 'COC fixed', insertGrains: 50,
      vaneGeometry: 'AAE Talon', vaneOffsetDeg: 2, vaneCount: 3, tuneQualityYawDeg: 2,
      measuredVelocityFps: 275,
    },
    env: {
      windMph: 12, windAngleDeg: 90, altitudeFt: 0,
      shotElevationDeg: 0, shotScenario: 'broadside-near',
    },
    rangeYards: 50,
  },
  {
    id: 'mech-quartering-into-30',
    title: 'Mech at quartering-into 30°',
    subtitle: 'Why the same build fails depending on shot scenario',
    category: 'plains-game',
    buildA: {
      ...BASE_HUNTING_BOW, arrowGrains: 500, arrowLengthIn: 28.5, spine: 300,
      fOCPercent: 14, pointGrains: 100, pointType: 'hybrid mech', insertGrains: 50,
      vaneGeometry: 'AAE Talon', vaneOffsetDeg: 2, vaneCount: 3, tuneQualityYawDeg: 2,
    },
    buildB: {
      ...BASE_HUNTING_BOW, arrowGrains: 500, arrowLengthIn: 28.5, spine: 300,
      fOCPercent: 14, pointGrains: 100, pointType: 'single-bevel', insertGrains: 50,
      vaneGeometry: 'AAE Talon', vaneOffsetDeg: 2, vaneCount: 3, tuneQualityYawDeg: 2,
    },
    env: {
      windMph: 5, windAngleDeg: 90, altitudeFt: 2000,
      shotElevationDeg: 0, shotScenario: 'quartering-into',
    },
    rangeYards: 35,
  },
  {
    id: 'three-arrow-weights',
    title: '380 / 450 / 525gr at 100y in 15 mph',
    subtitle: 'Where heavier stops paying',
    category: 'western-elk',
    buildA: {
      ...BASE_HUNTING_BOW, arrowGrains: 380, arrowLengthIn: 28, spine: 340,
      fOCPercent: 12, pointGrains: 100, pointType: 'single-bevel', insertGrains: 50,
      vaneGeometry: 'DCA Sabre', vaneOffsetDeg: 2, vaneCount: 3, tuneQualityYawDeg: 2,
    },
    buildB: {
      ...BASE_HUNTING_BOW, arrowGrains: 525, arrowLengthIn: 28.5, spine: 250,
      fOCPercent: 17, pointGrains: 150, pointType: 'single-bevel', insertGrains: 75,
      vaneGeometry: 'DCA Sabre', vaneOffsetDeg: 2, vaneCount: 3, tuneQualityYawDeg: 2,
    },
    env: {
      windMph: 15, windAngleDeg: 90, altitudeFt: 8000,
      shotElevationDeg: 0, shotScenario: 'broadside-near',
    },
    rangeYards: 100,
  },
];

interface StarterScenariosProps {
  onLoad: (scenario: Scenario) => void;
}

export function StarterScenarios({ onLoad }: StarterScenariosProps) {
  return (
    <div className="starter-scenarios">
      <h3>Scenarios</h3>
      <p className="scenarios-hint">One-click load — useful for fast comparison.</p>
      <div className="scenario-grid">
        {SCENARIOS.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`scenario-card scenario-${s.category}`}
            onClick={() => onLoad(s)}
            title={s.subtitle}
          >
            <span className="scenario-title">{s.title}</span>
            <span className="scenario-subtitle">{s.subtitle}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export { SCENARIOS };
